import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/api/client";
import type { DiseaseAreaRead } from "@/api/types";

/** DiseaseAreas this site is enrolled in. Reads the
 * ``site_disease_area`` table directly; participating locations under
 * each DA are managed inside the per-DA tab. */
export function useSiteEnrolledDiseaseAreas(siteId: string | undefined) {
  return useQuery<DiseaseAreaRead[]>({
    queryKey: ["sites", siteId, "enrolled-disease-areas"],
    queryFn: () =>
      apiRequest<DiseaseAreaRead[]>(`/api/sites/${siteId}/disease-areas`),
    enabled: Boolean(siteId),
  });
}

/** Enroll the site in a disease area (site-level, no location). The
 * new DA tab becomes available immediately; the operator adds
 * participating locations from inside that tab. */
export function useEnrollSiteInDiseaseArea(siteId: string) {
  const qc = useQueryClient();
  return useMutation<DiseaseAreaRead, Error, { daId: string }>({
    mutationFn: ({ daId }) =>
      apiRequest<DiseaseAreaRead>(`/api/sites/${siteId}/disease-areas/${daId}`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["sites", siteId, "enrolled-disease-areas"],
      });
    },
  });
}

export function useUnenrollSiteFromDiseaseArea(siteId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, { daId: string; reason: string }>({
    mutationFn: ({ daId, reason }) =>
      apiRequest<void>(`/api/sites/${siteId}/disease-areas/${daId}`, {
        method: "DELETE",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: ["sites", siteId, "enrolled-disease-areas"],
      });
    },
  });
}
